// Columna izquierda de la vista Reglas: el historial de reglas del retailer. Solo una lleva el
// badge «Activa» — es la que el motor usa hoy para repartir la cartera.
import { Badge, Button, ErrorState, Loading } from '@/kit';
import { userMessageOf } from '@/api/errors';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { describeStrategy, NO_THRESHOLD_LABEL } from '../logic';
import type { RuleRow } from '../types';

export interface RulesListProps {
    rules: readonly RuleRow[];
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    selectedRuleId: string | null;
    onSelect: (ruleId: string) => void;
    canCreateRule: boolean;
    onCreate: () => void;
}

export function RulesList({
    rules,
    isLoading,
    error,
    onRetry,
    selectedRuleId,
    onSelect,
    canCreateRule,
    onCreate,
}: RulesListProps) {
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-10 flex-none items-center justify-between border-b border-border px-3 py-2">
                <span className="text-[12px] font-semibold">Reglas de asignación</span>
                {canCreateRule ? (
                    <Button size="sm" className="h-6 text-[11px]" onClick={onCreate}>
                        Nueva regla
                    </Button>
                ) : null}
            </div>

            {isLoading ? (
                <Loading text="Cargando reglas…" />
            ) : error ? (
                <ErrorState
                    className="m-3"
                    title="No se pudieron cargar las reglas"
                    description={userMessageOf(error)}
                    onRetry={onRetry}
                />
            ) : rules.length === 0 ? (
                <p className="p-4 text-[12px] text-muted-foreground">
                    Esta cadena todavía no tiene reglas de asignación. Mientras no haya ninguna
                    activa, el motor reparte con sus valores por omisión.
                </p>
            ) : (
                <ul className="min-h-0 flex-1 overflow-y-auto">
                    {rules.map((rule) => {
                        const selected = rule.ruleId === selectedRuleId;
                        return (
                            <li key={rule.ruleId}>
                                <button
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => {
                                        onSelect(rule.ruleId);
                                    }}
                                    className={cn(
                                        'flex w-full flex-col gap-1 border-b border-border/50 px-3 py-2 text-left transition-colors',
                                        selected ? 'bg-success-soft/55' : 'hover:bg-accent/40',
                                    )}
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="text-[12.5px] font-semibold">
                                            {describeStrategy(rule.strategy)}
                                        </span>
                                        {rule.active ? (
                                            <Badge variant="success" size="xs">
                                                Activa
                                            </Badge>
                                        ) : null}
                                    </span>
                                    <span className="text-[11px] text-muted-foreground">
                                        Tope:{' '}
                                        {rule.maxLoadPerCollector === null
                                            ? NO_THRESHOLD_LABEL
                                            : rule.maxLoadPerCollector}
                                        {' · '}
                                        {formatDateMx(rule.updatedAt)}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
